import { ArrowRight, Target, Users, Lightbulb } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import Breadcrumbs from '../components/Breadcrumbs';
import BreadcrumbsJsonLd from '../components/BreadcrumbsJsonLd';

export default function AboutUs() {
  useSEO({
    title: 'Mission & Vision | GreenTick—The Interviewing Brain',
    description: 'GreenTick is on a mission to eliminate "gut feel" from hiring. Learn how our AI-powered behavioral capture system is revolutionizing talent intelligence and global recruitment.',
    canonicalPath: '/about',
  });
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumbs 
              items={[{ name: 'About the Engine', item: '/about' }]} 
            />

            <BreadcrumbsJsonLd 
              items={[
                { name: 'Home', item: '/' },
                { name: 'About', item: '/about' }
              ]}
            />

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                GreenTick
              </span>
            </h1>

            <p className="text-xl text-slate-300 mb-12 leading-relaxed">
              An AI-Powered Solution to Streamline Your Hiring Process – Identify, Engage, and Select the Best Talent.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="glass p-8 rounded-2xl text-center">
                <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <Target className="w-8 h-8 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Our Mission</h3>
                <p className="text-slate-400">
                  To revolutionize recruitment by making it faster, smarter, and more equitable through AI technology.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl text-center">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <Lightbulb className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Our Vision</h3>
                <p className="text-slate-400">
                  To become the global standard for AI-powered talent assessment and recruitment solutions.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl text-center">
                <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <Users className="w-8 h-8 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Our Values</h3>
                <p className="text-slate-400">
                  Innovation, fairness, transparency, and excellence in everything we do.
                </p>
              </div>
            </div>

            <div className="glass p-12 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">What We Do</h2>
              <div className="space-y-6 text-slate-300 text-lg leading-relaxed">
                <p>
                  GreenTick leverages advanced AI algorithms to screen resumes, conduct interactive interviews, and provide real-time feedback. This reduces the time and effort required for initial screenings, improves candidate engagement, and provides actionable insights for hiring managers.
                </p>
                <p>
                  Our conversational AI conducts interactive interviews by asking context-aware and adaptive questions based on the candidate's responses. This provides a personalized and engaging interview experience, helping to better assess the candidate's skills and suitability for the role.
                </p>
                <p>
                  We believe in making the hiring process as seamless and engaging as possible. Our platform provides you with actionable insights and analytics, helping you understand your candidate pool better and make strategic hiring choices.
                </p>
              </div>
            </div>

            <div className="glass p-12 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">Why Choose GreenTick?</h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-teal-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-slate-300 text-lg">
                    <span className="text-white font-semibold">10X Faster Hiring:</span> Reduce time-to-hire dramatically with automated screening and intelligent assessments.
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-slate-300 text-lg">
                    <span className="text-white font-semibold">Bias-Free Selection:</span> Remove unconscious bias from the hiring process with AI-driven evaluations.
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-teal-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-slate-300 text-lg">
                    <span className="text-white font-semibold">Data-Driven Decisions:</span> Make informed hiring choices with comprehensive analytics and insights.
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-slate-300 text-lg">
                    <span className="text-white font-semibold">Enhanced Candidate Experience:</span> Provide instant feedback and personalized interactions that leave a lasting impression.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://dashboard.greentick.org.in/auth"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition-all hover:scale-105 flex items-center gap-2 justify-center"
              >
                Get Started
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="px-8 py-4 glass text-white rounded-xl font-semibold hover:bg-white/20 transition-all hover:scale-105 flex items-center justify-center"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
